import { Box, Typography, useMediaQuery } from '@mui/material';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { Button } from 'src/components/button';
import { Section } from 'src/components/section';
import { AcknowledgeSummaryBox } from 'src/features/agreements/acknowledge-summary-box';
import { AgreementDrawer } from 'src/features/agreements/agreement-drawer';
import { theme } from 'src/styles/theme';
import { useHandleQueryError } from 'src/lib/hooks/use-handle-query-error';

import { AgreementFormState, AgreementType } from '../agreements/types';

import { styles } from './styles';

type OccupantAgreementsViewProps = {
    onGoBack: () => void;
    hideBackButton: boolean;

    loading: boolean;
    error: Error | null;

    isButtonDisabled: boolean;

    terms: AgreementFormState;
    licence: AgreementFormState;

    onSubmit: () => void;
    drawer: {
        open: boolean;
        current: AgreementType;
        agreement: AgreementType;
        onClose: () => void;
        total: number;
    };

    agreements: {
        terms: React.ReactNode;
        licence: React.ReactNode;
    };
    showTermsDrawer: () => void;
    showLicenceDrawer: () => void;
    academicYear: string;
};

export const OccupantAgreementsView: FC<OccupantAgreementsViewProps> = ({
    onGoBack,
    hideBackButton,
    loading,
    error,
    isButtonDisabled,
    onSubmit,
    terms,
    licence,
    drawer,
    agreements,
    showTermsDrawer,
    showLicenceDrawer,
    academicYear,
}) => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const isBookingTermsSigned = terms.checkboxVal && !!terms.email && terms.isValid;

    const isLicenceAgreementSigned = licence.checkboxVal && !!licence.email && licence.isValid;
    const handleQueryError = useHandleQueryError();

    if (error) {
        handleQueryError(error, academicYear);
    }

    return (
        <Box css={styles.agreementLayout}>
            {!isSmallScreen && !hideBackButton && (
                <Box css={styles.back} onClick={onGoBack}>
                    <BackArrow css={styles.icon} />
                    <Typography>{t('agreements_back_button_content')}</Typography>
                </Box>
            )}

            <Section header={t('agreements_title')}>
                {!isSmallScreen ? (
                    <Typography variant="body1">{t('agreements_sub_text')}</Typography>
                ) : (
                    <Typography variant="body3">{t('agreements_subheader')}</Typography>
                )}

                {isSmallScreen && (
                    <Box css={styles.wrapBox}>
                        <AcknowledgeSummaryBox
                            signed={isBookingTermsSigned}
                            content={t('agreements_summary_box_booking_terms_conditions')}
                            showAgreementDrawer={showTermsDrawer}
                        />
                        <AcknowledgeSummaryBox
                            signed={isLicenceAgreementSigned}
                            content={t('agreements_summary_box_licence_agreement')}
                            showAgreementDrawer={showLicenceDrawer}
                        />
                    </Box>
                )}

                {/* Drawer */}
                <AgreementDrawer
                    open={drawer.open}
                    closeFn={drawer.onClose}
                    currentAgreement={drawer.agreement}
                    totalAgreements={drawer.total}
                >
                    {drawer.current === AgreementType.Terms ? agreements.terms : agreements.licence}
                </AgreementDrawer>

                {/* Desktop agreements */}
                {!isSmallScreen && (
                    <Fragment>
                        {agreements.terms}
                        {agreements.licence}
                    </Fragment>
                )}
            </Section>

            <Button
                variant="contained"
                css={styles.cta}
                disabled={isButtonDisabled}
                onClick={onSubmit}
                loading={loading}
                id="submit-button"
            >
                {t('agreements_cta_complete_booking')}
            </Button>

            <Box css={styles.backWrapper}>
                {isSmallScreen && (
                    <Box css={[styles.back, styles.extraPadding]} onClick={onGoBack}>
                        {t('agreements_back_content')}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
